import type { CalendarDate } from "@internationalized/date";
import { useEffect, useRef } from "react";
import type { CalendarHandle } from "../calendar/use-calendar-handle";
import type { FocusState } from "./range";

type Tuple<T> = [T, T];

/**
 * Determines which month should be targeted for scrolling based on focus and selection state
 */
const getTargetMonth = (
  selectedDates: Tuple<CalendarDate | undefined>,
  focusState: FocusState,
  options?: { requireFocus?: boolean },
): CalendarDate | undefined => {
  const requireFocus = options?.requireFocus === true;
  const [startSelected, endSelected] = selectedDates;
  const anyFocused = focusState !== "none";

  if (requireFocus && !anyFocused) {
    return undefined;
  }

  if (!startSelected && !endSelected) {
    return undefined;
  }

  if (startSelected && !endSelected) {
    return startSelected;
  }

  if (startSelected && endSelected) {
    if (focusState === "start") {
      return startSelected;
    }
    if (focusState === "end") {
      return endSelected;
    }
    return startSelected;
  }

  return undefined;
};

/**
 * Simplified calendar scroll hook using the new FocusState enum
 */
export const useCalendarScroll = (
  isOpen: boolean,
  isMobile: boolean,
  focusState: FocusState,
  selectedDates: Tuple<CalendarDate | undefined>,
  calendarReference: React.RefObject<CalendarHandle | null>,
) => {
  const wasOpen = useRef<boolean>(false);

  useEffect(() => {
    const handle = calendarReference.current;
    const justOpened = isOpen && !wasOpen.current;
    const anyFocused = focusState !== "none";

    // Only scroll when: opening OR any input currently focused
    const shouldScroll = justOpened || anyFocused;
    if (!shouldScroll || !handle) {
      wasOpen.current = isOpen;
      return;
    }

    const target = getTargetMonth(selectedDates, focusState, {
      requireFocus: !justOpened,
    });
    if (!target) {
      wasOpen.current = isOpen;
      return;
    }

    // On open: always align to target month instantly
    if (justOpened) {
      handle.scrollToMonth(target, { behavior: "auto" });
      wasOpen.current = isOpen;
      return;
    }

    // After open: only align if the month isn't currently visible
    const monthInView = handle.isMonthInView(target, {
      partial: isMobile,
      tolerance: 2,
    });
    if (isMobile || !monthInView) {
      handle.scrollToMonth(target, { behavior: "smooth" });
    }

    wasOpen.current = isOpen;
  }, [isOpen, isMobile, focusState, selectedDates[0], selectedDates[1]]);
};
